import { useState, useEffect } from 'react';
import { api, getErrorMessage } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Receipt, CreditCard, Printer, User, Bed, UtensilsCrossed, Briefcase, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import PaymentButton from '@/components/PaymentButton';
import MultiPaymentInput from '@/components/MultiPaymentInput';

export default function BillingCheckout() {
  const [bookings, setBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [billDetails, setBillDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutDialogOpen, setCheckoutDialogOpen] = useState(false);
  const [payments, setPayments] = useState([]);
  const [paymentAmount, setPaymentAmount] = useState('');

  useEffect(() => {
    loadBookings();
  }, []);

  const loadBookings = async () => {
    try {
      const response = await api.get('/bookings?status=checked_in');
      setBookings(response.data);
    } catch (error) {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const loadBillDetails = async (bookingId) => {
    try {
      const response = await api.get(`/bookings/${bookingId}/bill-details`);
      const data = response.data;
      
      // Normalize API response to expected structure
      // Backend returns flat structure, frontend expects nested 'breakdown' object
      const normalizedData = {
        booking: data.booking,
        room: data.room,
        guest: data.guest,
        breakdown: {
          nights: data.room_charges?.nights || 0,
          price_per_night: data.room_charges?.rate_per_night || data.room?.price_per_night || 0,
          room_charges: data.room_charges?.total || 0,
          restaurant_orders: data.restaurant_orders || [],
          restaurant_charges: data.restaurant_total || 0,
          service_charges_list: data.service_charges || [],
          service_charges: data.services_total || 0,
          subtotal: data.subtotal || 0,
          tax_percentage: data.tax_rate || 18,
          tax: data.tax || 0,
          total_amount: data.grand_total || 0,
          advance_paid: data.advance_paid || 0,
          balance_due: data.balance_due || 0
        }
      };
      
      setBillDetails(normalizedData);
      setSelectedBooking(bookingId);
      setPaymentAmount((normalizedData.breakdown.balance_due || 0).toString());
    } catch (error) {
      toast.error('Failed to load bill details');
    }
  };

  const handleCheckout = async () => {
    try {
      // Format payment transactions
      const paymentTransactions = payments.map(p => ({
        method: p.method,
        amount: parseFloat(p.amount) || 0,
        transaction_id: p.transaction_id || null,
        card_last_four: p.card_last_four || null,
        card_type: p.card_type || null,
        upi_id: p.upi_id || null,
        bank_name: p.bank_name || null,
        bank_reference: p.bank_reference || null
      }));

      const totalPaid = payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);

      // First generate invoice
      const invoiceRes = await api.post(`/invoices?booking_id=${selectedBooking}`);
      
      // Process payment with transactions
      await api.post('/invoices/payment', {
        invoice_id: invoiceRes.data.id,
        amount: totalPaid,
        payment_transactions: paymentTransactions
      });

      // Checkout booking
      await api.patch(`/bookings/${selectedBooking}/checkout`);
      
      toast.success('Checkout completed successfully!');
      setCheckoutDialogOpen(false);
      setBillDetails(null);
      setSelectedBooking(null);
      setPayments([]);
      loadBookings();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Checkout failed'));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  return (
    <div className="space-y-6" data-testid="billing-checkout">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Manrope' }}>Billing & Checkout</h1>
        <p className="text-muted-foreground mt-1">Process guest checkout and billing</p>
      </div>

      {loading ? (
        <div className="text-center py-12">Loading active bookings...</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Booking List */}
          <div className="lg:col-span-1 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Active Bookings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {bookings.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">No active bookings</p>
                ) : (
                  bookings.map((booking) => (
                    <Card
                      key={booking.id}
                      className={`cursor-pointer hover:bg-accent transition-all ${
                        selectedBooking === booking.id ? 'border-primary border-2' : ''
                      }`}
                      onClick={() => loadBillDetails(booking.id)}
                      data-testid={`booking-item-${booking.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <p className="font-semibold" style={{ fontFamily: 'JetBrains Mono' }}>
                              {booking.id.slice(0, 8)}
                            </p>
                            <Badge className="bg-green-100 text-green-700">Active</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Check-in: {new Date(booking.check_in).toLocaleDateString()}
                          </p>
                          <p className="text-sm font-medium">
                            {formatCurrency(booking.total_amount)}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </CardContent>
            </Card>
          </div>

          {/* Bill Details */}
          <div className="lg:col-span-2">
            {!billDetails ? (
              <Card>
                <CardContent className="py-24 text-center">
                  <Receipt className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Select a booking to view bill details</p>
                </CardContent>
              </Card>
            ) : !billDetails.breakdown ? (
              <Card>
                <CardContent className="py-24 text-center">
                  <Receipt className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Loading bill breakdown...</p>
                </CardContent>
              </Card>
            ) : (
              <Card data-testid="bill-details-card">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Bill Details</CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">
                        Booking #{billDetails.booking?.id?.slice(0, 8) || 'N/A'}
                      </p>
                    </div>
                    <Button
                      onClick={() => setCheckoutDialogOpen(true)}
                      data-testid="proceed-checkout-button"
                    >
                      <Receipt className="h-4 w-4 mr-2" />
                      Proceed to Checkout
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Guest & Room Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <User className="h-4 w-4" />
                        <span>Guest Details</span>
                      </div>
                      <div>
                        <p className="font-semibold">{billDetails.guest?.name || 'N/A'}</p>
                        <p className="text-sm text-muted-foreground">{billDetails.guest?.phone || ''}</p>
                        {billDetails.guest?.email && (
                          <p className="text-sm text-muted-foreground">{billDetails.guest.email}</p>
                        )}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Bed className="h-4 w-4" />
                        <span>Room Details</span>
                      </div>
                      <div>
                        <p className="font-semibold">
                          Room {billDetails.room?.room_number || 'N/A'}
                        </p>
                        <p className="text-sm text-muted-foreground">{billDetails.room?.room_type || ''}</p>
                        <p className="text-sm text-muted-foreground">
                          {billDetails.breakdown?.nights || 0} night{(billDetails.breakdown?.nights || 0) > 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                  </div>

                  <Separator />

                  {/* Room Charges */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Bed className="h-5 w-5 text-primary" />
                      <h3 className="font-semibold">Room Charges</h3>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>{billDetails.breakdown?.nights || 0} nights × {formatCurrency(billDetails.breakdown?.price_per_night || 0)}</span>
                        <span className="font-semibold" style={{ fontFamily: 'JetBrains Mono' }}>
                          {formatCurrency(billDetails.breakdown?.room_charges || 0)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Restaurant Charges */}
                  {billDetails.breakdown?.restaurant_orders?.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <UtensilsCrossed className="h-5 w-5 text-primary" />
                        <h3 className="font-semibold">Restaurant Charges</h3>
                      </div>
                      <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                        {billDetails.breakdown.restaurant_orders.map((order, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span>
                              Order #{order.id?.slice(0, 8) || idx} {order.table_number && `(Table ${order.table_number})`}
                            </span>
                            <span className="font-semibold" style={{ fontFamily: 'JetBrains Mono' }}>
                              {formatCurrency(order.total_amount || 0)}
                            </span>
                          </div>
                        ))}
                        <Separator className="my-2" />
                        <div className="flex justify-between font-semibold">
                          <span>Total Restaurant</span>
                          <span style={{ fontFamily: 'JetBrains Mono' }}>
                            {formatCurrency(billDetails.breakdown?.restaurant_charges || 0)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Service Charges */}
                  {billDetails.breakdown?.service_charges_list?.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <Briefcase className="h-5 w-5 text-primary" />
                        <h3 className="font-semibold">Service Charges</h3>
                      </div>
                      <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                        {billDetails.breakdown.service_charges_list.map((charge, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span>{charge.service_name || 'Service'} × {charge.quantity || 1}</span>
                            <span className="font-semibold" style={{ fontFamily: 'JetBrains Mono' }}>
                              {formatCurrency(charge.amount || 0)}
                            </span>
                          </div>
                        ))}
                        <Separator className="my-2" />
                        <div className="flex justify-between font-semibold">
                          <span>Total Services</span>
                          <span style={{ fontFamily: 'JetBrains Mono' }}>
                            {formatCurrency(billDetails.breakdown?.service_charges || 0)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  <Separator />

                  {/* Total Calculation */}
                  <div className="space-y-3">
                    <div className="flex justify-between text-base">
                      <span>Subtotal</span>
                      <span className="font-semibold" style={{ fontFamily: 'JetBrains Mono' }}>
                        {formatCurrency(billDetails.breakdown?.subtotal || 0)}
                      </span>
                    </div>
                    <div className="flex justify-between text-base">
                      <span>GST ({billDetails.breakdown?.tax_percentage || 18}%)</span>
                      <span className="font-semibold" style={{ fontFamily: 'JetBrains Mono' }}>
                        {formatCurrency(billDetails.breakdown?.tax || 0)}
                      </span>
                    </div>
                    <Separator />
                    <div className="flex justify-between text-xl font-bold">
                      <span>Total Amount</span>
                      <span style={{ fontFamily: 'JetBrains Mono' }} data-testid="total-amount">
                        {formatCurrency(billDetails.breakdown?.total_amount || 0)}
                      </span>
                    </div>
                    <div className="flex justify-between text-base text-muted-foreground">
                      <span>Advance Paid</span>
                      <span className="font-semibold" style={{ fontFamily: 'JetBrains Mono' }}>
                        {formatCurrency(billDetails.breakdown?.advance_paid || 0)}
                      </span>
                    </div>
                    <div className="flex justify-between text-xl font-bold text-primary">
                      <span>Balance Due</span>
                      <span style={{ fontFamily: 'JetBrains Mono' }} data-testid="balance-due">
                        {formatCurrency(billDetails.breakdown?.balance_due || 0)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Checkout Dialog */}
      <Dialog open={checkoutDialogOpen} onOpenChange={setCheckoutDialogOpen}>
        <DialogContent className="max-w-md" data-testid="checkout-dialog">
          <DialogHeader>
            <DialogTitle>Process Checkout</DialogTitle>
          </DialogHeader>
          {billDetails && billDetails.breakdown && (
            <div className="space-y-4">
              <div className="bg-muted rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Total Amount</span>
                  <span className="font-semibold" style={{ fontFamily: 'JetBrains Mono' }}>
                    {formatCurrency(billDetails.breakdown?.total_amount || 0)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Advance Paid</span>
                  <span className="font-semibold" style={{ fontFamily: 'JetBrains Mono' }}>
                    {formatCurrency(billDetails.breakdown?.advance_paid || 0)}
                  </span>
                </div>
                <Separator />
                <div className="flex justify-between text-lg font-bold">
                  <span>Balance Due</span>
                  <span style={{ fontFamily: 'JetBrains Mono' }}>
                    {formatCurrency(billDetails.breakdown?.balance_due || 0)}
                  </span>
                </div>
              </div>

              {/* Multi-Payment Input */}
              <MultiPaymentInput
                totalAmount={billDetails.breakdown?.balance_due || 0}
                payments={payments}
                onPaymentsChange={setPayments}
              />

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setCheckoutDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleCheckout}
                  disabled={payments.length === 0 || payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0) < (billDetails.breakdown?.balance_due || 0) - 0.01}
                  data-testid="complete-checkout-btn"
                >
                  <Receipt className="h-4 w-4 mr-2" />
                  Complete Checkout
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}